import {useCreateProspectTagMutation, usePoolQuery} from 'api';
import {useFormik} from 'formik';
import * as Yup from 'yup';
import {tagColors} from 'helpers/tagColors';
import React, {useMemo} from 'react';
import styled from 'styled-components';
import {Button, Modal, Form, Input} from 'components/antd';
import {CheckIcon} from '@heroicons/react/20/solid';

interface Props {
  visible: boolean;
  onClose: () => void;
}

export default ({visible, onClose}: Props) => {
  const {data: {pool = {}, pool: {prospectTags = []} = {}} = {}} =
    usePoolQuery();

  const [createProspectTag] = useCreateProspectTagMutation({
    refetchQueries: [`pool`],
    onCompleted: () => onClose(),
  });

  const formik = useFormik({
    initialValues: {
      name: '',
      color: tagColors[0].hex,
    },
    validationSchema: Yup.object().shape({
      name: Yup.string().required('必須項目です'),
    }),
    onSubmit: (values, {resetForm}) => {
      createProspectTag({
        variables: {
          attributes: {
            ...values,
            poolId: pool.uuid,
          },
        },
      });
      resetForm();
    },
  });

  const alreadyNameExist = useMemo(
    () => prospectTags?.some((tag) => tag.name === formik.values.name),
    [formik],
  );

  return (
    <Modal
      destroyOnClose
      visible={visible}
      width={480}
      centered
      transitionName=""
      maskTransitionName=""
      footer={null}
      onCancel={onClose}>
      <ModalContent>
        <h2>タグを新規作成</h2>
        <Form layout="vertical" onFinish={formik.handleSubmit}>
          <Form.Item label="タグ名" required>
            <Input
              size="large"
              name="name"
              value={formik.values.name}
              onChange={formik.handleChange}
            />
            {alreadyNameExist && (
              <span className="text-red-500 mt-2">
                既に同じ名前のタグが存在します
              </span>
            )}
          </Form.Item>
          <Form.Item label="タグの色を選択">
            {tagColors.map((color, index) => (
              <div
                key={index}
                className="w-[34px] h-[34px] rounded-md cursor-pointer inline-block mr-4 mb-2 relative"
                style={{
                  backgroundColor: color.hex,
                }}
                onClick={() => {
                  formik.setFieldValue('color', color.hex);
                }}>
                {formik.values.color === color.hex && (
                  <CheckIcon className="w-6 h-6 absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 text-white" />
                )}
              </div>
            ))}
          </Form.Item>
          <Actions>
            <Button size="large" onClick={onClose}>
              キャンセル
            </Button>
            <Button
              type="primary"
              size="large"
              htmlType="submit"
              disabled={!formik.isValid || alreadyNameExist}>
              作成
            </Button>
          </Actions>
        </Form>
      </ModalContent>
    </Modal>
  );
};

const ModalContent = styled.div`
  h2 {
    font-size: 18px;
    font-weight: 700;
  }

  form {
    margin-top: 1rem;
  }

  label {
    font-weight: bold;
  }
`;

const Actions = styled.div`
  display: flex;
  justify-content: space-between;
`;
